import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import authService from './authService';

let tokenPromise = null;

const getToken = () => {
  if (!tokenPromise) {
    tokenPromise = authService.getIdToken().finally(() => {
      tokenPromise = null;
    });
  }
  return tokenPromise;
};

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 40000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[apiClient] Could not retrieve auth token:',
          err?.message,
        );
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(
        `[apiClient] --> ${config.method?.toUpperCase()} ${config.url}`,
      );
    }

    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[apiClient] <-- ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    const status = error.response?.status ?? null;
    const isNetworkError = !error.response;

    if (process.env.NODE_ENV === 'development') {
      console.error(
        `[apiClient] <-- ${status ?? 'NETWORK_ERROR'} ${error.config?.url}`,
        error.response?.data ?? error.message,
      );
    }

    switch (true) {
      case status === 401:
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        enqueueSnackbar('Session expired. Please log in again.', {
          variant: 'error',
        });
        break;
      case status === 403:
        enqueueSnackbar("You don't have permission to perform this action.", {
          variant: 'error',
        });
        break;
      case status !== null && status >= 500:
        enqueueSnackbar('Server error. Please try again later.', {
          variant: 'error',
        });
        break;
      case isNetworkError:
        enqueueSnackbar('Network error. Please check your connection.', {
          variant: 'error',
        });
        break;
      default: {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'An unexpected error occurred.';
        enqueueSnackbar(msg, { variant: 'error' });
      }
    }

    return Promise.reject({
      status,
      message:
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'An unexpected error occurred.',
      data: error.response?.data ?? null,
      isNetworkError,
      isForbidden: status === 403,
      isServerError: status !== null && status >= 500,
    });
  },
);

export default apiClient;
